/**
 * DomainsCard — manages Resend sender domains: add, list, expand for DNS records,
 * verify, delete. Extracted from EmailConfigPage.js to keep that page under a
 * maintainable size.
 *
 * Props:
 *   domains          : array of domain objects {id, name, status, region, created_at}
 *   newDomain        : controlled value for the "add domain" input
 *   setNewDomain     : setter
 *   adding           : boolean — add-in-flight spinner
 *   expandedDomain   : id of currently-expanded domain or null
 *   domainRecords    : map of {domain_id: {records: [...]}} (lazy-loaded)
 *   onRefresh        : () => void — refetch the domains list
 *   onAddDomain      : () => Promise<void>
 *   onToggleExpanded : (id) => Promise<void>
 *   onVerify         : (id, name) => Promise<void>
 *   onDelete         : (id, name) => Promise<void>
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Plus, Loader2, RefreshCcw, Globe, Shield, Trash2, Copy, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

const STATUS_COLORS = {
  verified: '#34C759',
  pending: '#FF9500',
  not_started: '#9CA3AF',
  failed: '#FF3B30',
  temporary_failure: '#FF9500',
};

function DnsRow({ record }) {
  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success('Copied to clipboard');
  };
  const color = STATUS_COLORS[record.status] || '#9CA3AF';
  return (
    <div className="bg-white border border-[#E5E7EB] rounded p-2 text-[10px] font-mono">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Badge className="text-[8px]" style={{ backgroundColor: color + '20', color }}>{record.type}</Badge>
          {record.status && <span className="text-[9px]" style={{ color }}>{record.status}</span>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[80px_1fr_auto] gap-2 items-start">
        <span className="text-[#9CA3AF]">Name</span>
        <span className="break-all text-[#0A0A0A]">{record.name}</span>
        <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => copy(record.name)}><Copy className="w-3 h-3" /></Button>

        <span className="text-[#9CA3AF]">Value</span>
        <span className="break-all text-[#0A0A0A]">{record.value}</span>
        <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => copy(record.value)}><Copy className="w-3 h-3" /></Button>

        {record.ttl !== undefined && (
          <>
            <span className="text-[#9CA3AF]">TTL</span>
            <span className="text-[#0A0A0A]">{record.ttl}</span>
            <span />
          </>
        )}
        {record.priority !== undefined && (
          <>
            <span className="text-[#9CA3AF]">Priority</span>
            <span className="text-[#0A0A0A]">{record.priority}</span>
            <span />
          </>
        )}
      </div>
    </div>
  );
}

const DomainsCard = ({
  domains,
  newDomain,
  setNewDomain,
  adding,
  expandedDomain,
  domainRecords,
  onRefresh,
  onAddDomain,
  onToggleExpanded,
  onVerify,
  onDelete,
}) => {
  return (
    <Card className="border-[#E5E7EB]" data-testid="domains-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#002FA7]" />
            Verified Domains ({domains.length})
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={onRefresh} data-testid="refresh-domains-btn">
            <RefreshCcw className="w-3 h-3 mr-1" />Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Add domain */}
        <div className="flex gap-2 mb-3 p-3 bg-[#FAFAFA] rounded-lg" data-testid="add-domain-form">
          <Input
            value={newDomain}
            onChange={e => setNewDomain(e.target.value)}
            placeholder="yourdomain.com"
            className="h-9 text-sm flex-1"
            data-testid="new-domain-input"
            onKeyDown={e => e.key === 'Enter' && onAddDomain()}
          />
          <Button
            onClick={onAddDomain}
            disabled={adding || !newDomain}
            className="bg-[#002FA7] hover:bg-[#00227A] h-9"
            data-testid="add-domain-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Add Domain
          </Button>
        </div>

        {domains.length === 0 ? (
          <div className="text-center py-8 text-[#9CA3AF]">
            <Globe className="w-10 h-10 mx-auto mb-2 text-[#E5E7EB]" />
            <p className="text-sm">No domains registered yet. Add one above to get started.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {domains.map(d => {
              const color = STATUS_COLORS[d.status] || '#9CA3AF';
              const expanded = expandedDomain === d.id;
              const details = domainRecords[d.id];
              return (
                <div key={d.id} className="border border-[#E5E7EB] rounded-lg" data-testid={`domain-${d.id}`}>
                  <div className="p-3 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Badge className="text-[9px] flex-shrink-0" style={{ backgroundColor: color + '20', color }}>
                        {d.status.replace('_', ' ')}
                      </Badge>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#0A0A0A] truncate">{d.name}</p>
                        <p className="text-[10px] text-[#9CA3AF]">
                          Region: {d.region} · Created {format(new Date(d.created_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm" variant="ghost" className="h-7 text-[10px]"
                        onClick={() => onToggleExpanded(d.id)}
                        data-testid={`toggle-domain-${d.id}`}
                      >
                        {expanded ? 'Hide' : 'DNS Records'}
                      </Button>
                      <Button
                        size="sm" variant="ghost" className="h-7 text-[10px] text-[#002FA7]"
                        onClick={() => onVerify(d.id, d.name)}
                        data-testid={`verify-domain-${d.id}`}
                      >
                        <Shield className="w-3 h-3 mr-0.5" />Verify
                      </Button>
                      <Button
                        size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#FF3B30]"
                        onClick={() => onDelete(d.id, d.name)}
                        data-testid={`delete-domain-${d.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  {expanded && (
                    <div className="border-t border-[#E5E7EB] p-3 bg-[#FAFAFA]" data-testid={`records-${d.id}`}>
                      {!details ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <>
                          <p className="text-[11px] font-medium text-[#4B5563] mb-2">
                            Add these DNS records at your registrar, then click "Verify":
                          </p>
                          <div className="space-y-2">
                            {(details.records || []).map((rec, idx) => (
                              <DnsRow key={idx} record={rec} />
                            ))}
                            {(!details.records || details.records.length === 0) && (
                              <p className="text-xs text-[#9CA3AF]">No DNS records returned by Resend.</p>
                            )}
                          </div>
                          <a
                            href={`https://resend.com/domains/${d.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] text-[#002FA7] hover:underline mt-3 inline-flex items-center"
                          >
                            Open in Resend dashboard <ExternalLink className="w-3 h-3 ml-0.5" />
                          </a>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DomainsCard;
